import { useState } from 'react'
import type { ArtifactIssue } from '../api'

const pad3 = (n: number) => String(n).padStart(3, '0')

/**
 * Multi-issue email marker (Ken/Emerson 2026-08-03 spec). Rendered only when
 * an email raises more than one issue. Two modes:
 *
 *  - `unscoped` (Inbox/unfiled): the enumerated issue list. It stands in for a
 *    whole-email summary — a summary of three unrelated legal problems is mush.
 *    Each row: ordinal · label · optional distinguishing clause (the L3 excerpt).
 *
 *  - `scoped` (a matter's chronology): the "same email also raises" block shown
 *    below the matter-scoped summary. It lists ONLY the sibling issues — the
 *    matter we're currently inside is excluded (a 3-issue email shows 2). Its
 *    job is to contradict the summary's implied completeness. Sibling rows are
 *    DISPLAY-ONLY (no navigation), by design — awareness, not a jump target.
 *
 * Type is deliberately a touch smaller than the chronology text (Emerson
 * 2026-08-03) — this is supplementary heads-up info, never bigger than the
 * body. No hue, no icon, no badge; the signal is textual because it's a number.
 * `collapsed` = the row's `details` toggle is off.
 */
export function MultiIssueBlock({
  issues,
  mode,
  currentMatterId,
  collapsed = false,
}: {
  issues: ArtifactIssue[]
  mode: 'unscoped' | 'scoped'
  currentMatterId?: number | null
  collapsed?: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const frame = 'mt-2 border-l me-border pl-[14px]'
  // Single source for the heads-up label size so the collapsed one-liner
  // ("also raises N other issues") and the expanded label ("same email also
  // raises") can never drift apart (Emerson 2026-08-03).
  const headsUp = 'text-[10px] me-text-mute'

  if (mode === 'unscoped') {
    // State A — the list IS the summary. Same when collapsed (details off):
    // the visible rows make a "N issues" count redundant, so no count line here.
    return (
      <div className={frame}>
        {issues.map((iss) => (
          <div
            key={iss.issue_index}
            className="mb-[4px] flex gap-2 text-[10px] leading-[1.45] last:mb-0"
          >
            <span className="me-mono shrink-0 me-text-mute">{iss.issue_index}</span>
            <span className="min-w-0 line-clamp-2">
              <span className="me-text">{iss.label}</span>
              {iss.excerpt && <span className="me-text-mute"> · {iss.excerpt}</span>}
            </span>
          </div>
        ))}
      </div>
    )
  }

  // State B — siblings only: exclude the matter we're inside, and dedup by
  // matter (an email with two issues on the same matter shows it once).
  const seen = new Set<number>()
  const siblings = issues.filter((i) => {
    if (i.matter_id === currentMatterId) return false
    if (i.matter_id != null) {
      if (seen.has(i.matter_id)) return false
      seen.add(i.matter_id)
    }
    return true
  })
  if (siblings.length === 0) return null

  // Details off: one line in place of the full block; click expands locally.
  if (collapsed && !expanded) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(true) }
        }}
        className={`mt-1 cursor-pointer ${headsUp} hover:me-text`}
      >
        also raises {siblings.length} other issue{siblings.length === 1 ? '' : 's'}
      </div>
    )
  }

  return (
    <div className={frame}>
      <div className={`mb-[4px] ${headsUp}`}>same email also raises</div>
      {siblings.map((s) => (
        <div
          key={s.issue_index}
          className="mb-[4px] flex items-baseline gap-2 text-[10px] last:mb-0"
        >
          <span className="min-w-0 line-clamp-2 me-text">{s.label}</span>
          <span className="shrink-0 me-text-mute">
            {s.matter_client_seq != null ? `M-${pad3(s.matter_client_seq)}` : '—'}
          </span>
        </div>
      ))}
    </div>
  )
}
